import {
  ArrowLeft,
  ArrowRight,
  Check,
  MessageCircle,
} from "lucide-react";
import {
  useState,
  type FormEvent,
} from "react";

import {
  submitNovaServiceEnquiry,
  type NovaServiceType,
} from "./nova-guest-operations";

const SERVICES: Array<{
  value: NovaServiceType;
  label: string;
}> = [
  {
    value: "birthday_cake",
    label: "Birthday Cakes",
  },
  {
    value: "wedding_cake",
    label: "Wedding Cakes",
  },
  {
    value: "celebration_cake",
    label: "Celebration / Custom Cakes",
  },
  {
    value: "outside_catering",
    label: "Outside Catering",
  },
  {
    value: "corporate_lunches",
    label: "Corporate Office Lunches",
  },
  {
    value: "meeting_snacks",
    label: "Meeting Snacks / Tea Breaks",
  },
  {
    value: "private_functions",
    label: "Private Dinners / Functions",
  },
  {
    value: "birthday_celebration",
    label: "Birthdays & Celebrations",
  },
  {
    value: "wedding_reception",
    label: "Weddings / Receptions",
  },
  {
    value: "corporate_event",
    label: "Corporate Events",
  },
  {
    value: "other",
    label: "Other Hospitality Enquiry",
  },
];

const INITIAL_FORM = {
  name: "",
  phone: "",
  email: "",
  serviceType:
    "birthday_cake" as NovaServiceType,
  eventDate: "",
  guestCount: "",
  budget: "",
  location: "",
  preferredTime: "",
  requirements: "",
  dietaryRequirements: "",
  cakeSize: "",
  cakeFlavour: "",
  cakeMessage: "",
  cakeFulfilment: "",
};

function InputLabel({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <span className="mb-2 block text-[9px] font-semibold uppercase tracking-[0.2em] text-[#718076]">
      {children}
    </span>
  );
}

const inputClass =
  "w-full border-b border-[#b5bbae] bg-transparent py-3 text-sm text-[#314a3b] outline-none placeholder:text-[#a2aaa0] focus:border-[#9d725c]";

export function NovaServiceEnquiryPage() {
  const requestedService =
    new URLSearchParams(window.location.search).get(
      "service",
    ) as NovaServiceType | null;

  const allowedServices = SERVICES.map(
    (service) => service.value,
  );

  const initialService =
    requestedService &&
    allowedServices.includes(requestedService)
      ? requestedService
      : INITIAL_FORM.serviceType;

  const [form, setForm] =
    useState({
      ...INITIAL_FORM,
      serviceType: initialService,
    });
  const [busy, setBusy] =
    useState(false);
  const [sent, setSent] =
    useState(false);
  const [error, setError] =
    useState<string | null>(null);

  const isCakeService = [
    "birthday_cake",
    "wedding_cake",
    "celebration_cake",
  ].includes(form.serviceType);

  function update<K extends keyof typeof form>(
    key: K,
    value: (typeof form)[K],
  ) {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  }

  async function handleSubmit(
    event: FormEvent<HTMLFormElement>,
  ) {
    event.preventDefault();

    if (busy) {
      return;
    }

    setBusy(true);
    setError(null);

    const cakeDetails = isCakeService
      ? [
          form.cakeSize
            ? `Cake size: ${form.cakeSize}`
            : "",
          form.cakeFlavour
            ? `Flavour: ${form.cakeFlavour}`
            : "",
          form.cakeMessage
            ? `Message on cake: ${form.cakeMessage}`
            : "",
          form.cakeFulfilment
            ? `Fulfilment: ${form.cakeFulfilment}`
            : "",
        ]
          .filter(Boolean)
          .join("\n")
      : "";

    const requirements = [
      cakeDetails,
      form.requirements.trim()
        ? `${
            isCakeService
              ? "Customer notes:\n"
              : ""
          }${form.requirements.trim()}`
        : "",
    ]
      .filter(Boolean)
      .join("\n\n");

    const result =
      await submitNovaServiceEnquiry({
        ...form,
        requirements,
      });

    setBusy(false);

    if (!result.ok) {
      setError(
        result.error ||
          "We could not send your enquiry.",
      );
      return;
    }

    setSent(true);
  }

  return (
    <main className="min-h-screen bg-[#eee9dc] text-[#314a3b]">
      <header className="border-b border-[#b5bbae] px-5 py-5 sm:px-8">
        <div className="mx-auto flex max-w-[1280px] items-center justify-between gap-6">
          <button
            type="button"
            onClick={() => {
              window.location.href = "/";
            }}
            className="flex items-center gap-3 text-[9px] font-semibold uppercase tracking-[0.18em] text-[#718076]"
          >
            <ArrowLeft size={14} />
            Back to Nova
          </button>

          <img
            src="/images/nova/logo2.png"
            onError={(event) => {
              event.currentTarget.src =
                "/images/nova/novatransparent.png";
            }}
            alt="Nova Res Culina"
            className="h-14 w-auto object-contain sm:h-16"
          />
        </div>
      </header>

      <section className="mx-auto grid min-h-[calc(100vh-90px)] max-w-[1280px] gap-12 px-5 py-12 sm:px-8 lg:grid-cols-[.8fr_1.2fr] lg:gap-20 lg:py-20">
        <div className="lg:pt-10">
          <p className="text-[9px] font-semibold uppercase tracking-[0.24em] text-[#9d725c]">
            At Nova
          </p>

          <h1 className="mt-6 font-['Cormorant_Garamond'] text-6xl font-light leading-[0.9] sm:text-7xl">
            Tell us what
            <br />
            you&apos;re planning.
          </h1>

          <p className="mt-8 max-w-md text-sm leading-7 text-[#667067]">
            Cakes, celebrations, outside
            catering, office lunches,
            meeting snacks and private
            gatherings — share the shape of
            the occasion and the Nova team
            will reach out.
          </p>

          <div className="mt-12 border-t border-[#b5bbae] pt-6">
            <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#718076]">
              Prefer to talk?
            </p>

            <a
              href="https://wa.me/254719275522"
              className="mt-4 inline-flex items-center gap-3 text-sm text-[#9d725c]"
            >
              <MessageCircle size={16} />
              WhatsApp Nova
            </a>
          </div>
        </div>

        <div className="bg-[#f4efe4] p-6 sm:p-10 lg:p-12">
          {sent ? (
            <div className="flex min-h-[620px] flex-col items-center justify-center text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full border border-[#9d725c] text-[#9d725c]">
                <Check
                  size={25}
                  strokeWidth={1.4}
                />
              </span>

              <p className="mt-8 text-[10px] font-semibold uppercase tracking-[0.24em] text-[#9d725c]">
                Enquiry received
              </p>

              <h2 className="mt-4 font-['Cormorant_Garamond'] text-5xl leading-none">
                We&apos;ll take it
                <br />
                from here.
              </h2>

              <p className="mt-6 max-w-sm text-sm leading-6 text-[#667067]">
                Your details have reached
                the Nova team. Someone will
                contact you to shape the
                next steps.
              </p>

              <button
                type="button"
                onClick={() => {
                  setForm({
                    ...INITIAL_FORM,
                    serviceType: initialService,
                  });
                  setSent(false);
                }}
                className="mt-9 border-b border-[#9d725c] pb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#9d725c]"
              >
                Send another enquiry
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="space-y-7"
            >
              <div className="border-b border-[#b5bbae] pb-7">
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#9d725c]">
                  Plan with Nova
                </p>

                <h2 className="mt-3 font-['Cormorant_Garamond'] text-4xl leading-none">
                  Give us the details.
                </h2>
              </div>

              <label className="block">
                <InputLabel>
                  Your name
                </InputLabel>
                <input
                  required
                  value={form.name}
                  onChange={(event) =>
                    update(
                      "name",
                      event.target.value,
                    )
                  }
                  className={inputClass}
                />
              </label>

              <div className="grid gap-6 sm:grid-cols-2">
                <label className="block">
                  <InputLabel>
                    Phone / WhatsApp
                  </InputLabel>
                  <input
                    required
                    type="tel"
                    value={form.phone}
                    onChange={(event) =>
                      update(
                        "phone",
                        event.target.value,
                      )
                    }
                    className={inputClass}
                  />
                </label>

                <label className="block">
                  <InputLabel>
                    Email
                  </InputLabel>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(event) =>
                      update(
                        "email",
                        event.target.value,
                      )
                    }
                    className={inputClass}
                  />
                </label>
              </div>

              <label className="block">
                <InputLabel>
                  What can we help with?
                </InputLabel>

                <select
                  value={form.serviceType}
                  onChange={(event) =>
                    update(
                      "serviceType",
                      event.target
                        .value as NovaServiceType,
                    )
                  }
                  className={inputClass}
                >
                  {SERVICES.map((service) => (
                    <option
                      key={service.value}
                      value={service.value}
                    >
                      {service.label}
                    </option>
                  ))}
                </select>
              </label>

              {isCakeService && (
                <div className="space-y-7 border-y border-[#b5bbae] py-7">
                  <div>
                    <p className="text-[9px] font-semibold uppercase tracking-[0.22em] text-[#9d725c]">
                      Cake details
                    </p>
                    <p className="mt-2 text-xs leading-5 text-[#718076]">
                      Tell us the size and finish you have in mind.
                      If you are unsure, Nova can help you choose.
                    </p>
                  </div>

                  <div className="grid gap-6 sm:grid-cols-2">
                    <label className="block">
                      <InputLabel>
                        Cake size (kg)
                      </InputLabel>

                      <select
                        value={form.cakeSize}
                        onChange={(event) =>
                          update(
                            "cakeSize",
                            event.target.value,
                          )
                        }
                        className={inputClass}
                      >
                        <option value="">
                          Select cake size
                        </option>
                        <option value="0.5 kg">
                          0.5 kg
                        </option>
                        <option value="1 kg">
                          1 kg
                        </option>
                        <option value="1.5 kg">
                          1.5 kg
                        </option>
                        <option value="2 kg">
                          2 kg
                        </option>
                        <option value="2.5 kg">
                          2.5 kg
                        </option>
                        <option value="3 kg">
                          3 kg
                        </option>
                        <option value="4 kg">
                          4 kg
                        </option>
                        <option value="5 kg">
                          5 kg
                        </option>
                        <option value="Custom / Not sure">
                          Custom / Not sure
                        </option>
                      </select>
                    </label>

                    <label className="block">
                      <InputLabel>
                        Flavour
                      </InputLabel>

                      <input
                        value={form.cakeFlavour}
                        onChange={(event) =>
                          update(
                            "cakeFlavour",
                            event.target.value,
                          )
                        }
                        placeholder="e.g. Vanilla, chocolate, red velvet"
                        className={inputClass}
                      />
                    </label>
                  </div>

                  <div className="grid gap-6 sm:grid-cols-2">
                    <label className="block">
                      <InputLabel>
                        Message on cake
                      </InputLabel>

                      <input
                        value={form.cakeMessage}
                        onChange={(event) =>
                          update(
                            "cakeMessage",
                            event.target.value,
                          )
                        }
                        placeholder="e.g. Happy Birthday Rose"
                        className={inputClass}
                      />
                    </label>

                    <label className="block">
                      <InputLabel>
                        Pickup / Delivery
                      </InputLabel>

                      <select
                        value={form.cakeFulfilment}
                        onChange={(event) =>
                          update(
                            "cakeFulfilment",
                            event.target.value,
                          )
                        }
                        className={inputClass}
                      >
                        <option value="">
                          Select option
                        </option>
                        <option value="Pickup">
                          Pickup
                        </option>
                        <option value="Delivery">
                          Delivery
                        </option>
                      </select>
                    </label>
                  </div>
                </div>
              )}

              <div className="grid gap-6 sm:grid-cols-2">
                <label className="block">
                  <InputLabel>
                    Event / delivery date
                  </InputLabel>
                  <input
                    type="date"
                    value={form.eventDate}
                    onChange={(event) =>
                      update(
                        "eventDate",
                        event.target.value,
                      )
                    }
                    className={inputClass}
                  />
                </label>

                <label className="block">
                  <InputLabel>
                    {isCakeService
                      ? "Number of people (optional)"
                      : "Number of people"}
                  </InputLabel>
                  <input
                    type="number"
                    min="1"
                    max="10000"
                    value={form.guestCount}
                    onChange={(event) =>
                      update(
                        "guestCount",
                        event.target.value,
                      )
                    }
                    placeholder="e.g. 40"
                    className={inputClass}
                  />
                </label>
              </div>

              <div className="grid gap-6 sm:grid-cols-2">
                <label className="block">
                  <InputLabel>
                    Budget range
                  </InputLabel>
                  <input
                    value={form.budget}
                    onChange={(event) =>
                      update(
                        "budget",
                        event.target.value,
                      )
                    }
                    placeholder="e.g. KES 30,000–50,000"
                    className={inputClass}
                  />
                </label>

                <label className="block">
                  <InputLabel>
                    Location / venue
                  </InputLabel>
                  <input
                    value={form.location}
                    onChange={(event) =>
                      update(
                        "location",
                        event.target.value,
                      )
                    }
                    placeholder="Where is it happening?"
                    className={inputClass}
                  />
                </label>
              </div>

              <label className="block">
                <InputLabel>
                  Preferred time
                </InputLabel>
                <input
                  value={form.preferredTime}
                  onChange={(event) =>
                    update(
                      "preferredTime",
                      event.target.value,
                    )
                  }
                  placeholder="e.g. Delivery by 12:30 PM"
                  className={inputClass}
                />
              </label>

              <label className="block">
                <InputLabel>
                  Tell us about the occasion
                </InputLabel>
                <textarea
                  required
                  rows={4}
                  value={form.requirements}
                  onChange={(event) =>
                    update(
                      "requirements",
                      event.target.value,
                    )
                  }
                  placeholder="Cake theme, service style, menu ideas, setup, occasion, special requests..."
                  className={`${inputClass} resize-none`}
                />
              </label>

              <label className="block">
                <InputLabel>
                  Dietary requirements
                  <span className="ml-2 font-normal normal-case tracking-normal">
                    (optional)
                  </span>
                </InputLabel>
                <textarea
                  rows={2}
                  value={
                    form.dietaryRequirements
                  }
                  onChange={(event) =>
                    update(
                      "dietaryRequirements",
                      event.target.value,
                    )
                  }
                  className={`${inputClass} resize-none`}
                />
              </label>

              {error && (
                <div className="border border-[#9d725c]/30 bg-[#9d725c]/8 px-4 py-3 text-xs leading-5 text-[#7d5544]">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={busy}
                className="flex w-full items-center justify-between bg-[#314a3b] px-5 py-4 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-[#f3ead7] transition-colors hover:bg-[#9d725c] disabled:cursor-wait disabled:opacity-60"
              >
                <span>
                  {busy
                    ? "Sending enquiry…"
                    : "Send enquiry"}
                </span>

                <ArrowRight size={16} />
              </button>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
